import type { ReactNode } from "react";

import type { CredentialVerification } from "~/lib/shared/cco-claims";

export type TrustBandCoach = {
  profile: {
    primaryCity: string | null;
    languages: string | null;
    coachingSinceYear?: number | null;
  };
  /** Flat CCO membership objects from CoachPublicData. */
  ccoMemberships?: {
    ccoName: string;
    verification: CredentialVerification | null;
  }[];
  /** Flat CTP certification objects from CoachPublicData. */
  ctpCertifications?: {
    ctpName: string;
    verification: CredentialVerification | null;
  }[];
  certifications: { title: string; verified: boolean }[];
};

function pickTopCredential(coach: TrustBandCoach): string | null {
  const cco = coach.ccoMemberships?.find((m) => m.verification !== null);
  if (cco) return cco.ccoName;
  const ctp = coach.ctpCertifications?.find((c) => c.verification !== null);
  if (ctp) return ctp.ctpName;
  const manual = coach.certifications.find((c) => c.verified);
  if (manual) return manual.title;
  return null;
}

function pickLocationLine(coach: TrustBandCoach): string | null {
  const parts = [coach.profile.primaryCity, coach.profile.languages].filter(
    (p): p is string => Boolean(p && p.trim().length > 0),
  );
  return parts.length > 0 ? parts.join(" · ") : null;
}

export function CoachTrustBand({
  coach,
  rating,
}: {
  coach: TrustBandCoach;
  /** Aggregate of engagement-verified client ratings, if any. */
  rating?: { average: number; count: number } | null;
}): ReactNode {
  const credential = pickTopCredential(coach);
  const year = coach.profile.coachingSinceYear ?? null;
  const location = pickLocationLine(coach);

  const items = [
    credential,
    year != null ? `Coaching since ${year}` : null,
    location,
  ].filter((s): s is string => Boolean(s));

  const hasRating = rating != null && rating.count > 0;
  if (items.length + (hasRating ? 1 : 0) < 2) return null;

  return (
    <div className="border-y border-gray-200">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-4 text-[13px] text-gray-600 xl:px-0">
        {hasRating && (
          <span className="flex items-center gap-2">
            <span aria-hidden className="text-sm text-amber-400">
              ★
            </span>
            <span className="font-medium text-gray-900 tabular-nums">
              {rating.average.toFixed(1)}
            </span>
            <span>
              from {rating.count} verified client
              {rating.count === 1 ? "" : "s"}
            </span>
          </span>
        )}
        {items.map((item) => (
          <span key={item} className="flex items-center gap-2">
            <span
              aria-hidden
              className="size-[5px] rounded-full bg-purple-300"
            />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}
